import React from 'react';
import styled from 'astroturf/react';
import DownloadIcon from 'js/design_system/icons/arrow_downward.svg';
import LinkIcon from 'js/design_system/icons/arrow_right.svg';
import { Button } from 'js/design_system/Button';
import { ButtonLink } from 'js/design_system/link/ButtonLink';
import { Modal } from 'js/design_system/Modal';
import { Attachment } from 'js/liswp/build_vu/Attachment';
import { PaperUpdateUnlinked } from '../shared/types';
import { Props as ImpressionProps } from 'js/utils/impressions/ImpressionWatcher';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';

const ReaderModal = styled(Modal)<{ fullWidth: boolean }>`
  > :global(.ds2-5-modal__container) {
    height: 100vh;
    width: 100vw;
  }

  @import 'stylesheets/design_system/_breakpoint.scss';

  &:not(.fullWidth) {
    @include breakpoint(medium) {
      > :global(.ds2-5-modal__container) {
        height: calc(100vh - 40px);
        width: calc(100vw - 80px);
      }
    }

    @include breakpoint(large) {
      > :global(.ds2-5-modal__container) {
        height: calc(100vh - 63px);
        width: calc(100vw - 156px);
        max-width: 1128px;
      }
    }
  }
`;

const AttachmentPanel = styled.div`
  height: 100%;
  flex-grow: 1;
  overflow-y: auto;
  overflow-x: hidden;
`;

const ContentsWrapper = styled.div`
  flex: 1;
  overflow: hidden;
  background: #f4f7fc;

  display: flex;
  flex-direction: row;
  gap: 48px;
`;

const AttachmentWrapper = styled.div`
  flex: 1;
  background: #fff;
  min-width: 0px;

  display: flex;
  flex-direction: column;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 24px;

  @include for-tablet-up {
    flex-direction: row;

    div {
      display: flex;
      flex: 1;

      button,
      a {
        width: 100%;
      }
    }
  }
`;

type ButtonRowProps = {
  work: PaperUpdateUnlinked;
  impressionProps?: ImpressionProps;
  onDownload?: () => void;
  source: string;
};

export const DefaultButtonRow = ({
  work,
  impressionProps,
  onDownload,
  source,
}: ButtonRowProps) => (
  <ButtonRow>
    <ImpressionWatcher {...impressionProps} source={`${source}_download`}>
      <Button icon={<DownloadIcon />} secondary onClick={onDownload}>
        Download
      </Button>
    </ImpressionWatcher>
    <ImpressionWatcher {...impressionProps} source={`${source}_related`}>
      <ButtonLink href={work.url} target="_blank" iconRight={<LinkIcon />}>
        View Related Papers
      </ButtonLink>
    </ImpressionWatcher>
  </ButtonRow>
);

export type ReaderProps = {
  className?: string;
  work: PaperUpdateUnlinked;
  open: boolean;
  impressionProps?: ImpressionProps;
  onDownload?: () => void;
  onClose?: () => void;
  buttonRow?: React.ReactElement;
  sidebar?: React.ReactElement;
  fullWidth?: boolean;
};

export const Reader: React.FC<ReaderProps> = ({
  className,
  work,
  open,
  impressionProps,
  onDownload,
  onClose = () => null,
  buttonRow = React.createElement(DefaultButtonRow),
  sidebar,
  fullWidth = false,
}) => {
  const baseSource = impressionProps?.source;
  const source = (baseSource ? `${baseSource}_` : '') + 'reader';

  return (
    <ReaderModal
      className={className}
      title={work.title}
      open={open}
      onClose={onClose}
      fullWidth={fullWidth}
    >
      <ContentsWrapper>
        <AttachmentWrapper>
          <AttachmentPanel>
            {work.attachment?.id && (
              <Attachment
                key={work.attachment.id.toString()}
                shown={open}
                attachmentId={work.attachment.id.toString()}
              />
            )}
          </AttachmentPanel>
          {React.cloneElement(buttonRow, {
            ...buttonRow.props,
            work,
            impressionProps,
            onDownload,
            source,
          })}
        </AttachmentWrapper>
        {sidebar}
      </ContentsWrapper>
    </ReaderModal>
  );
};
